import React, { useState, useEffect } from 'react';

const SuggestionManagement = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, implemented
  const [searchTerm, setSearchTerm] = useState('');

  // Use API base URL from env
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchSuggestions();
    // eslint-disable-next-line
  }, []);

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/admin/suggestions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSuggestionStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/admin/suggestions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, isRead: true })
      });

      if (response.ok) {
        fetchSuggestions();
      }
    } catch (error) {
      console.error('Error updating suggestion:', error);
    }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this suggestion?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/admin/suggestions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSuggestions();
      }
    } catch (error) {
      console.error('Error deleting suggestion:', error);
    }
  };

  const filteredSuggestions = suggestions
    .filter(s => filter === 'all' || s.status === filter)
    .filter(s => 
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.message.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'reviewed': return '#17a2b8';
      case 'implemented': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'reviewed': return '👀';
      case 'implemented': return '✅';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <div>Loading suggestions...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <h2 style={{ margin: 0, fontSize: '28px', color: '#1e293b' }}>
          💡 User Suggestions ({filteredSuggestions.length})
        </h2>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          {['all', 'pending', 'reviewed', 'implemented'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                background: filter === status ? '#3b82f6' : '#e2e8f0',
                color: filter === status ? 'white' : '#475569',
                cursor: 'pointer',
                fontWeight: 'bold',
                textTransform: 'capitalize',
                transition: 'all 0.3s'
              }}
            >
              {status}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search suggestions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            flex: 1,
            minWidth: '200px'
          }}
        />
      </div>

      {/* Suggestions List */}
      <div style={{ display: 'grid', gap: '20px' }}>
        {filteredSuggestions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f8fafc',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '15px' }}>📭</div>
            <div style={{ fontSize: '18px', color: '#64748b' }}>
              No suggestions found
            </div>
          </div>
        ) : (
          filteredSuggestions.map(suggestion => (
            <div
              key={suggestion._id}
              style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: `3px solid ${getStatusColor(suggestion.status)}`,
                transition: 'transform 0.3s'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '15px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    marginBottom: '8px'
                  }}>
                    📧 {suggestion.email}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: '#3b82f6',
                    fontWeight: 'bold',
                    marginBottom: '5px'
                  }}>
                    Category: {suggestion.category}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#64748b'
                  }}>
                    {new Date(suggestion.createdAt).toLocaleString()}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  background: getStatusColor(suggestion.status),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  {getStatusIcon(suggestion.status)} {suggestion.status}
                </div>
              </div>

              <div style={{
                background: '#f8fafc',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#334155'
              }}>
                {suggestion.message}
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                {suggestion.status !== 'pending' && (
                  <button
                    onClick={() => updateSuggestionStatus(suggestion._id, 'pending')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#ffc107',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    ⏳ Mark Pending
                  </button>
                )}
                
                {suggestion.status !== 'reviewed' && (
                  <button
                    onClick={() => updateSuggestionStatus(suggestion._id, 'reviewed')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#17a2b8',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    👀 Mark Reviewed
                  </button>
                )}

                {suggestion.status !== 'implemented' && (
                  <button
                    onClick={() => updateSuggestionStatus(suggestion._id, 'implemented')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#28a745',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    ✅ Mark Implemented
                  </button>
                )}

                <button
                  onClick={() => deleteSuggestion(suggestion._id)}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#dc3545',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    marginLeft: 'auto'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SuggestionManagement;
